// @ts-strict-ignore
import { Injectable, ViewContainerRef } from '@angular/core';
import { LMComponentInfoComponent } from '@components/componentInfo';
import { AppConstants, LMDialogButtonKey } from '@utils/index';
import { LMUniqueIdService } from '@utils/lm-unique-id.service';
import { CurrentTarget, LMCtrlShiftClickEvent } from '@base/index';
import { LMContextService } from './lm-context.service';
import { ComponentType, LMModalDialogWrapperRef, LMModalWrapperService } from '@services/modal';

@Injectable()
export class LMComponentInfoService {

  public dialog: LMModalDialogWrapperRef<any>;
  private buttons: SohoModalButton[];
  public compInfoDialogOpen = false;
  public additionalInfo: LMCompInfoAdditionalInfo = {};
  private myNotificationsUrl: string;

  constructor(
    protected lmUniqueIdService: LMUniqueIdService,
    private lmContextService: LMContextService,
    private lmModalWrapperService: LMModalWrapperService
  ) {}

  public displayComponentInfo(event: LMCtrlShiftClickEvent, placeHolder: ViewContainerRef, compInfo: LMComponentInfo): void {

    if ((!((event.ctrlKey || event.metaKey) && event.shiftKey)) && !event.isPage && !this.getCardviewSpec()) {
      return;
    }

    compInfo.panelSpec = this.getPanelSpec();
    compInfo.contextFormSpec = this.getContextFormSpec();
    compInfo.cardviewSpec = this.getCardviewSpec();
    compInfo.isDBSessionDebugActive = this.lmContextService.hasDBSessionDebugMode();
    compInfo.dbSessionId = this.lmContextService.getDBSessionDebugId();

    const compType = this.getComponentTypeFromEvent(event);

    // let placeHolder = this.getComponentPlaceHolder();
    // let compInfo: ComponentInfo = this.getComponentInfo();

    // if (!(compInfo.componentType === 'FIELD' && compInfo.specField === 'NONSPECFIELD')
    //   && (((compInfo.componentType === 'FORM' && compType.indexOf('FORM') < 0
    //   && compInfo.componentType !== compType)) {
    //   return;
    // }

    const dialogId = 'ComponentInfo';
    this.buttons = [
      {
        id: this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.CLOSE ]),
        text: Soho.Locale.translate(LMDialogButtonKey.CLOSE),
        click: (e, modal) => {
          modal.close();
        },
      }
    ];

    this.displayDialog(placeHolder, LMComponentInfoComponent, this.buttons, compInfo, Soho.Locale.translate('ComponentInfo'));
    this.clearAdditionalInfo();
  }

  private displayDialog(
    placeHolder: ViewContainerRef, component: ComponentType<any>, buttons: any, info: any, title: string): void {
    if (placeHolder && !this.compInfoDialogOpen) {
      this.compInfoDialogOpen = true;
      const settingsModalAttributes: SohoAttributesArray = [ {
        name: 'data-automation-id',
        value: 'lm-componentinfo-modal'
      } ];

      this.dialog = this.lmModalWrapperService
        .modal(component, { fullsize: 'responsive', attributes: settingsModalAttributes })
        .buttons(buttons)
        .title(title)
        .apply((c) => {
          c.info = info;
        })
        .open();
      // As a safety we will reset the flag after a dialog is opened and a couple of seconds have expired getting it ready for the next time.
      setTimeout(() => this.compInfoDialogOpen = false, 2000);
      this.dialog.afterClose(result => {
        this.compInfoDialogOpen = false;
      });
    }
  }

  public getComponentTypeFromEvent(event: LMCtrlShiftClickEvent): string {
    if (event.srcEventElement && this.closest(event.srcEventElement.firstChild, 'lm-html-url-doc')) {
      return 'HTMLDOC';
    }
    if (this.closest(event.currentTarget, 'lm-field') || this.hasClass(event.currentTarget, 'fieldset-title')) {
      return 'FIELD';
    }
    if (this.closest(event.currentTarget, 'lm-form-panel-form')) {
      return 'FORM_PANEL';
    }
    if (this.closest(event.currentTarget, 'lm-list')) {
      return 'LIST';
    }
    if (this.closest(event.currentTarget, 'lm-form')) {
      return 'FORM';
    }
    if (this.closest(event.currentTarget, 'lm-banner-menu')) {
      return 'MENU';
    }
    const currentTarget: CurrentTarget = event.currentTarget as CurrentTarget;
    if (currentTarget && currentTarget.nodeName && currentTarget.nodeName === 'LM-APPLICATION-MENU-HEADER') {   // tslint:disable-line
      let type = '';
      try {
        // tslint:disable
        type = 'form';
        // tslint:enable
      } catch (e) {}
      if (type === AppConstants.RESOURCETYPE_LIST) {
        return 'LIST';
      }
      if (type === AppConstants.RESOURCETYPE_PAGE) {
        return 'PAGE';
      }
      if (type === AppConstants.RESOURCETYPE_FORM) {
        return 'FORM';
      }
    }
    if (event.isPage) {
      return 'PAGE';
    }

    return null;
  }

  public closeDialog() {
    this.dialog.close();
  }

  public hasClass(element, className) {
    if (!element || element == null) {
      return false;
    }

    if (element.classList) {
      return element.classList.contains(className);
    } else {
      return new RegExp('(^| )' + className + '( |$)', 'gi').test(element.className);
    }
  }

  public hasConfigAccess() {
    return this.lmContextService.hasConfigAccess();
  }

  public closest(element, selector) {
    let matchesFn;

    // find vendor prefix
    [ 'matches', 'webkitMatchesSelector', 'mozMatchesSelector', 'msMatchesSelector', 'oMatchesSelector' ].some(function(fn) {
      if (typeof document.body[ fn ] === 'function') {
        matchesFn = fn;
        return true;
      }
      return false;
    });

    let parent;

    // traverse parents
    while (element) {
      parent = element.parentElement;
      if (parent && parent[ matchesFn ](selector)) {
        return parent;
      }
      element = parent;
    }
  }

  public buildCtrlShiftClickEvent(event: MouseEvent, additionInfo?: LMCompInfoAdditionalInfo): LMCtrlShiftClickEvent {
    const ctrlShiftClickEvent: LMCtrlShiftClickEvent = new LMCtrlShiftClickEvent(event.type);
    ctrlShiftClickEvent.originalEvent = event;
    ctrlShiftClickEvent.metaKey = event.metaKey;
    ctrlShiftClickEvent.ctrlKey = event.ctrlKey;
    ctrlShiftClickEvent.shiftKey = event.shiftKey;
    ctrlShiftClickEvent.additionInfo = additionInfo;

    return ctrlShiftClickEvent;
  }

  public clearAdditionalInfo() {
    this.additionalInfo.panelSpec = undefined;
    this.additionalInfo.contextFormSpec = undefined;
    this.additionalInfo.cardviewSpec = undefined;
  }

  public getMyNotificationsUrl(): string {
    return this.myNotificationsUrl;
  }

  public setMyNotificationsUrl(value: string) {
    this.myNotificationsUrl = value;
  }

  public setPanelSpec(panelSpec: LMCompositePanelSpec) {
    this.additionalInfo.panelSpec = panelSpec;
  }

  public getPanelSpec(): LMCompositePanelSpec {
    return this.additionalInfo.panelSpec;
  }

  public setContextFormSpec(contextFormSpec: LMBusFormSpec) {
    this.additionalInfo.contextFormSpec = contextFormSpec;
  }

  public getContextFormSpec(): LMBusFormSpec {
    return this.additionalInfo.contextFormSpec;
  }

  public setCardviewSpec(cardviewSpec: LMListDisplayField) {
    this.additionalInfo.cardviewSpec = cardviewSpec;
  }

  public getCardviewSpec(): LMListDisplayField {
    return this.additionalInfo.cardviewSpec;
  }
}
